import { useState, useEffect } from 'react';
import { Map } from '../components/Map';
import { BusMarkerComponent } from '../components/BusMarker';
import { RouteOverlay } from '../components/RouteOverlay';
import { ETADisplay } from '../components/ETADisplay';
import { BusSelector } from '../components/BusSelector';
import { useBusTracker } from '../hooks/useWebSocket';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { api } from '../lib/api';
import type { Bus, Student, Stop } from '../lib/types';

interface ParentHomeProps {
  onLogout: () => void;
  userName: string;
}

export function ParentHome({ onLogout, userName }: ParentHomeProps) {
  const [students, setStudents] = useState<Student[]>([]);
  const [buses, setBuses] = useState<Bus[]>([]);
  const [stops, setStops] = useState<Stop[]>([]);
  const [selectedBusId, setSelectedBusId] = useState<string | null>(null);
  const [studentStopId, setStudentStopId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const push = usePushNotifications();

  const { position, connected } = useBusTracker({
    busId: selectedBusId,
    enabled: !!selectedBusId,
  });

  // Load initial data
  useEffect(() => {
    async function loadData() {
      try {
        const [studentsRes, busesRes] = await Promise.all([
          api.getStudents(),
          api.getBuses(),
        ]);
        setStudents(studentsRes.students);
        setBuses(busesRes.buses);

        // Auto-select first bus
        if (studentsRes.students.length > 0 && studentsRes.students[0].bus_id) {
          const firstBusId = studentsRes.students[0].bus_id;
          setSelectedBusId(firstBusId);
          setStudentStopId(studentsRes.students[0].stop_id);
        }
      } catch {
        // Handle error
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Load route/stops when bus changes
  useEffect(() => {
    if (!selectedBusId) return;
    api.getBus(selectedBusId).then(({ stops: busStops }) => {
      if (busStops) setStops(busStops);
    }).catch(() => {});
  }, [selectedBusId]);

  const handleBusSelect = (busId: string) => {
    setSelectedBusId(busId);
    const student = students.find((s) => s.bus_id === busId);
    setStudentStopId(student?.stop_id || null);
  };

  const mapCenter: [number, number] | undefined = position
    ? [position.lat, position.lng]
    : stops.length > 0
    ? [stops[0].latitude, stops[0].longitude]
    : undefined;

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const selectedBus = buses.find((b) => b.id === selectedBusId);

  return (
    <div className="parent-home">
      {/* Header */}
      <header className="app-header">
        <div className="header-left">
          <span className="header-logo">🚌</span>
          <span className="header-title">Bus Tracker</span>
        </div>
        <div className="header-right">
          {push.supported && (
            <button
              className={`btn-push ${push.subscribed ? 'push-on' : ''}`}
              onClick={() => push.subscribed ? push.unsubscribe() : push.subscribe()}
              title={push.subscribed ? 'Notifications ON' : 'Enable notifications'}
            >
              {push.subscribed ? '🔔' : '🔕'}
            </button>
          )}
          <span className="header-user">{userName}</span>
          <button className="btn-logout" onClick={onLogout}>
            Logout
          </button>
        </div>
      </header>

      {/* Connection status */}
      <div className={`connection-status ${connected ? 'connected' : 'disconnected'}`}>
        {connected ? 'Live' : 'Connecting...'}
      </div>

      {/* Map */}
      <div className="map-container">
        <Map center={mapCenter}>
          {position && selectedBus && (
            <BusMarkerComponent
              position={position}
              busNumber={selectedBus.bus_number}
              driverName={selectedBus.driver_name}
            />
          )}
          <RouteOverlay stops={stops} highlightStopId={studentStopId} />
        </Map>
      </div>

      {/* Bottom panel */}
      <div className="bottom-panel">
        <ETADisplay
          position={position}
          stops={stops}
          studentStopId={studentStopId}
        />
        <BusSelector
          students={students}
          buses={buses}
          selectedBusId={selectedBusId}
          onSelect={handleBusSelect}
        />
      </div>
    </div>
  );
}
